import React, { useState, useEffect } from 'react';
import io from 'socket.io-client';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import GroupManager from './components/GroupManager';
import Contacts from './Contacts';
import { API_URL, SOCKET_URL } from './config';
import './App.css';

const socket = io(SOCKET_URL);

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function HomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState({ status: 'initializing', message: 'Initializing...' });
  const [number, setNumber] = useState('91');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [response, setResponse] = useState(null);
  const [apiInfo, setApiInfo] = useState(null);
  const [showApiInfo, setShowApiInfo] = useState(false);
  const [user, setUser] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [showGroups, setShowGroups] = useState(false);
  const [groups, setGroups] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showPhoneDropdown, setShowPhoneDropdown] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]); // Array of {type: 'phone'|'contact'|'group', value: string, label: string}
  const [templates, setTemplates] = useState([]);
  const [showTemplatesDropdown, setShowTemplatesDropdown] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateMessage, setTemplateMessage] = useState('');
  const [templateFile, setTemplateFile] = useState(null);
  const [templateFileType, setTemplateFileType] = useState(null);
  const [templateFilePreview, setTemplateFilePreview] = useState(null);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [messageDelay, setMessageDelay] = useState(2); // Default 2 seconds delay
  const [sendProgress, setSendProgress] = useState(null); // {current, total, percentage, status, currentRecipient, waitProgress}
  const [accounts, setAccounts] = useState([]); // List of all logged in WhatsApp accounts
  const [selectedAccountId, setSelectedAccountId] = useState('default'); // Selected account for sending messages

  // Set phone number from navigation state
  useEffect(() => {
    if (location.state?.phoneNumber) {
      setNumber(location.state.phoneNumber);
    }
  }, [location.state]);

  useEffect(() => {
    // Get user from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }

    // Listen for status updates - but verify with accounts before accepting non-ready status
    socket.on('status', (statusData) => {
      // Always accept ready status
      if (statusData.status === 'ready') {
        setStatus(statusData);
      } else {
        // For non-ready status, verify by checking accounts
        // This prevents showing QR prompt when other accounts are ready
        verifyStatusWithAccounts();
      }
    });

    // Listen for account ready events
    socket.on('account-ready', (data) => {
      // When any account becomes ready, update status
      setStatus({ status: 'ready', message: 'WhatsApp is connected and ready!' });
      fetchAccounts();
    });

    // Listen for account active changed events
    socket.on('account-active-changed', (data) => {
      console.log('Account active changed event received:', data);
      // Sync selectedAccountId with the new active account
      if (data.accountId) {
        setSelectedAccountId(data.accountId);
        console.log('✅ Updated selectedAccountId to active account:', data.accountId);
      }
      fetchAccounts(); // Refresh accounts list
    });

    // Listen for message progress updates
    socket.on('message-progress', (progressData) => {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      // Only update if progress is for current user
      if (progressData.userId === currentUser.id) {
        setSendProgress(progressData);
        
        // Clear progress when completed
        if (progressData.status === 'completed') {
          setTimeout(() => {
            setSendProgress(null);
          }, 2000);
        }
      }
    });

    // Check initial status
    checkStatus();
    
    // Periodically verify status with accounts (every 5 seconds)
    const statusCheckInterval = setInterval(() => {
      verifyStatusWithAccounts();
    }, 5000);

    return () => {
      socket.off('status');
      socket.off('account-ready');
      socket.off('account-active-changed');
      socket.off('message-progress');
      clearInterval(statusCheckInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Verify status by checking if any account is ready
  const verifyStatusWithAccounts = async () => {
    try {
      // Check status from API (which now checks all accounts)
      const res = await api.get('/api/status');
      if (res.data.ready) {
        // At least one account is ready, set status to ready
        setStatus({ status: 'ready', message: 'WhatsApp is connected and ready!' });
      } else {
        // No accounts are ready, check accounts list to be sure
        const accountsRes = await api.get('/api/accounts');
        if (accountsRes.data.success) {
          const accountsList = accountsRes.data.accounts || [];
          const hasReadyAccount = accountsList.some(acc => acc.isReady);
          if (hasReadyAccount) {
            setStatus({ status: 'ready', message: 'WhatsApp is connected and ready!' });
          } else {
            // No ready accounts, show not ready status
            setStatus({ status: 'not_ready', message: 'WhatsApp is not connected. Please scan QR code.' });
          }
        }
      }
    } catch (error) {
      console.error('Error verifying status:', error);
    }
  };

  useEffect(() => {
    if (status.status === 'ready') {
      fetchGroups();
      fetchContacts();
      fetchTemplates();
      fetchAccounts();
    } else {
      // Periodically verify if any account became ready
      // This prevents showing QR prompt when accounts are actually ready
      const verifyInterval = setInterval(() => {
        verifyStatusWithAccounts();
      }, 3000);
      return () => clearInterval(verifyInterval);
    }
  }, [status.status]);

  // Fetch all logged in accounts
  const fetchAccounts = async () => {
    try {
      const response = await api.get('/api/accounts');
      if (response.data.success) {
        const accountsList = response.data.accounts || [];
        setAccounts(accountsList);
        
        // Sync selectedAccountId with backend's activeAccountId
        const activeAccount = accountsList.find(acc => acc.isActive && acc.isReady);
        if (activeAccount) {
          // If backend has an active account, use it
          setSelectedAccountId(activeAccount.accountId);
          console.log('✅ Synced selectedAccountId with active account:', activeAccount.accountId);
        } else if (accountsList.length > 0) {
          // If no active account but accounts exist, select first available ready account
          const availableAccount = accountsList.find(acc => acc.isReady);
          if (availableAccount) {
            // Only update if current selectedAccountId is not available
            const currentAccountAvailable = accountsList.find(acc => acc.accountId === selectedAccountId && acc.isReady);
            if (!currentAccountAvailable) {
              setSelectedAccountId(availableAccount.accountId);
              console.log('✅ Updated selectedAccountId to first available account:', availableAccount.accountId);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  // Fetch accounts periodically
  useEffect(() => {
    if (status.status === 'ready') {
      fetchAccounts();
      const accountsInterval = setInterval(fetchAccounts, 5000); // Refresh every 5 seconds
      return () => clearInterval(accountsInterval);
    }
  }, [status.status]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPhoneDropdown && !event.target.closest('.phone-input-wrapper')) {
        setShowPhoneDropdown(false);
      }
      if (showTemplatesDropdown && !event.target.closest('.templates-wrapper')) {
        setShowTemplatesDropdown(false);
      }
      if (showSettings) {
        // Only close if clicking on overlay, not on the modal itself or its children
        if (event.target.classList.contains('settings-overlay')) {
          setShowSettings(false);
        } else if (!event.target.closest('.settings-dropdown')) {
          // If click is outside both overlay and modal, close it
          setShowSettings(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPhoneDropdown, showTemplatesDropdown, showSettings]);

  const fetchGroups = async () => {
    try {
      const response = await api.get('/api/groups');
      if (response.data.success) {
        setGroups(response.data.groups || []);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      // Don't show error if it's just 404 or empty groups
      if (error.response?.status !== 404) {
        setGroups([]);
      }
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await api.get('/api/contacts');
      if (response.data.success) {
        setContacts(response.data.contacts || []);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/api/templates');
      if (response.data.success) {
        setTemplates(response.data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    }
  };

  const formatPhoneNumber = (phoneNumber) => {
    // Remove any non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    // If it doesn't start with 91, add it
    if (cleaned && !cleaned.startsWith('91')) {
      return '91' + cleaned;
    }
    return cleaned || '91';
  };

  const handleGroupSelect = (group) => {
    // Add group to selected recipients if not already added
    const recipient = {
      type: 'group',
      value: group.id,
      label: group.name,
      group: group
    };
    
    if (!selectedRecipients.find(r => r.type === 'group' && r.value === group.id)) {
      setSelectedRecipients([...selectedRecipients, recipient]);
    }
    setShowPhoneDropdown(false);
  };

  const handleContactSelect = (contact) => {
    // Add contact to selected recipients if not already added
    const formattedNumber = formatPhoneNumber(contact.phone_number);
    const recipient = {
      type: 'contact',
      value: formattedNumber,
      label: `${contact.name} (${contact.phone_number})`,
      contact: contact
    };
    
    if (!selectedRecipients.find(r => r.type === 'contact' && r.value === formattedNumber)) {
      setSelectedRecipients([...selectedRecipients, recipient]);
    }
    setShowPhoneDropdown(false);
  };

  const handlePhoneNumberAdd = () => {
    // Add phone number from input to selected recipients
    const trimmedNumber = number?.trim();
    if (!trimmedNumber || trimmedNumber === '91') {
      return;
    }
    
    const formattedNumber = formatPhoneNumber(trimmedNumber);
    const recipient = {
      type: 'phone',
      value: formattedNumber,
      label: formattedNumber
    };
    
    if (!selectedRecipients.find(r => r.type === 'phone' && r.value === formattedNumber)) {
      setSelectedRecipients([...selectedRecipients, recipient]);
      setNumber('91'); // Reset input
    }
  };

  const handleRemoveRecipient = (index) => {
    setSelectedRecipients(selectedRecipients.filter((_, i) => i !== index));
  };

  const handleGroupCreated = () => {
    fetchGroups();
    fetchContacts();
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const checkStatus = async () => {
    try {
      const res = await api.get('/api/status');
      if (res.data.ready) {
        setStatus({ status: 'ready', message: 'WhatsApp is connected and ready!' });
        // Fetch API info when ready
        fetchApiInfo();
      } else {
        setStatus({ status: 'not_ready', message: 'WhatsApp is not connected. Please scan QR code.' });
      }
    } catch (error) {
      console.error('Error checking status:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      } else {
      setStatus({ status: 'error', message: 'Unable to connect to server' });
      }
    }
  };

  const fetchApiInfo = async () => {
    try {
      const res = await api.get('/api/info');
      setApiInfo(res.data);
    } catch (error) {
      console.error('Error fetching API info:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      
      // Determine file type
      if (file.type.startsWith('image/')) {
        setFileType('image');
        // Create preview for images
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else if (file.type.startsWith('audio/')) {
        setFileType('audio');
        setFilePreview(null);
      } else if (file.type.startsWith('video/')) {
        setFileType('video');
        setFilePreview(null);
      } else {
        setFileType('document');
        setFilePreview(null);
      }
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileType(null);
    setFilePreview(null);
    // Reset file input
    const fileInput = document.getElementById('file-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleTemplateFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTemplateFile(file);
      
      // Determine file type
      if (file.type.startsWith('image/')) {
        setTemplateFileType('image');
        // Create preview for images
        const reader = new FileReader();
        reader.onloadend = () => {
          setTemplateFilePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else if (file.type.startsWith('audio/')) {
        setTemplateFileType('audio');
        setTemplateFilePreview(null);
      } else if (file.type.startsWith('video/')) {
        setTemplateFileType('video');
        setTemplateFilePreview(null);
      } else {
        setTemplateFileType('document');
        setTemplateFilePreview(null);
      }
    }
  };

  const handleRemoveTemplateFile = () => {
    setTemplateFile(null);
    setTemplateFileType(null);
    setTemplateFilePreview(null);
    // Reset file input
    const templateFileInput = document.getElementById('template-file-input');
    if (templateFileInput) {
      templateFileInput.value = '';
    }
  };

  const handleCreateTemplate = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('=== handleCreateTemplate called ===');
    console.log('Template name:', templateName);
    console.log('Template message:', templateMessage);
    console.log('Template file:', templateFile);
    
    if (!templateName.trim()) {
      alert('Template name is required');
      return;
    }
    
    if (!templateMessage.trim() && !templateFile) {
      alert('Either template message or file attachment is required');
      return;
    }
    
    setIsCreatingTemplate(true);
    console.log('Starting template creation...');
    
    try {
      const formData = new FormData();
      formData.append('name', templateName.trim());
      formData.append('message', templateMessage.trim() || '');
      if (templateFile) {
        formData.append('file', templateFile);
        console.log('File attached:', templateFile.name, templateFile.size, 'bytes');
      }
      
      console.log('Sending POST request to /api/templates');
      const response = await api.post('/api/templates', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('Response received:', response.data);
      
      if (response.data.success) {
        console.log('✅ Template created successfully! ID:', response.data.template?.id);
        // Reset form
        setTemplateName('');
        setTemplateMessage('');
        handleRemoveTemplateFile();
        setShowTemplateModal(false);
        // Refresh templates list
        await fetchTemplates();
        alert('Template created successfully!');
      } else {
        console.error('❌ Template creation failed:', response.data);
        alert('Failed to create template: ' + (response.data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Error creating template:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to create template';
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsCreatingTemplate(false);
      console.log('=== handleCreateTemplate finished ===');
    }
  };

  const handleTemplateSelect = async (template) => {
    console.log('Template selected:', template);
    
    // Fill message from template
    if (template.message) {
      setMessage(template.message);
    } else {
      setMessage('');
    }
    
    // If template has a file, load it
    if (template.file_path && template.file_name) {
      try {
        console.log('Loading template file:', template.file_path);
        
        // Get file from server using axios with authentication
        // Handle both relative paths (uploads/...) and absolute paths (for backward compatibility)
        let fileUrl;
        if (template.file_path.startsWith('uploads/')) {
          // Relative path - use as is
          fileUrl = `${API_URL}/${template.file_path}`;
        } else if (template.file_path.startsWith('/')) {
          // Absolute path starting with / - use as is
          fileUrl = `${API_URL}${template.file_path}`;
        } else if (template.file_path.includes('\\') || template.file_path.includes(':')) {
          // Absolute Windows path - extract filename and use relative path
          const fileName = template.file_path.split(/[/\\]/).pop();
          fileUrl = `${API_URL}/uploads/${fileName}`;
        } else {
          // Assume relative path
          fileUrl = `${API_URL}/${template.file_path}`;
        }
        
        console.log('Fetching file from:', fileUrl);
        
        const response = await api.get(fileUrl, {
          responseType: 'blob'
        });
        
        // Convert response to blob
        const blob = response.data;
        
        // Determine MIME type based on file extension or template file_type
        let mimeType = blob.type || 'application/octet-stream';
        if (!mimeType || mimeType === 'application/octet-stream') {
          const ext = template.file_name.split('.').pop().toLowerCase();
          const mimeTypes = {
            'jpg': 'image/jpeg',
            'jpeg': 'image/jpeg',
            'png': 'image/png',
            'gif': 'image/gif',
            'pdf': 'application/pdf',
            'doc': 'application/msword',
            'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'mp3': 'audio/mpeg',
            'mp4': 'video/mp4',
            'avi': 'video/x-msvideo'
          };
          mimeType = mimeTypes[ext] || 'application/octet-stream';
        }
        
        // Create File object from blob
        const file = new File([blob], template.file_name, { type: mimeType });
        
        // Set file state
        setSelectedFile(file);
        
        // Determine file type and create preview
        if (template.file_type === 'image') {
          setFileType('image');
          // Create preview for images
          const reader = new FileReader();
          reader.onloadend = () => {
            setFilePreview(reader.result);
          };
          reader.readAsDataURL(file);
        } else if (template.file_type === 'audio') {
          setFileType('audio');
          setFilePreview(null);
        } else if (template.file_type === 'video') {
          setFileType('video');
          setFilePreview(null);
        } else {
          setFileType('document');
          setFilePreview(null);
        }
        
        console.log('✅ Template file loaded successfully:', file.name, file.size, 'bytes');
      } catch (error) {
        console.error('Error loading template file:', error);
        alert('Failed to load template file: ' + error.message);
        // Clear file states on error
        setSelectedFile(null);
        setFileType(null);
        setFilePreview(null);
      }
    } else {
      // No file in template, clear file states
      setSelectedFile(null);
      setFileType(null);
      setFilePreview(null);
    }
    
    setShowTemplatesDropdown(false);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    const trimmedMessage = message?.trim();
    const trimmedNumber = number?.trim();
    
    // Collect all recipients
    let allRecipients = [...selectedRecipients];
    
    // If phone number is entered manually, add it
    if (trimmedNumber && trimmedNumber !== '91') {
      const formattedNumber = formatPhoneNumber(trimmedNumber);
      if (!allRecipients.find(r => r.type === 'phone' && r.value === formattedNumber)) {
        allRecipients.push({
          type: 'phone',
          value: formattedNumber,
          label: formattedNumber
        });
      }
    }
    
    if (allRecipients.length === 0) {
      setResponse({ type: 'error', message: 'Please select at least one recipient (phone number, contact, or group)' });
      return;
    }

    if (!trimmedMessage && !selectedFile) {
      setResponse({ type: 'error', message: 'Either message or file attachment is required' });
      return;
    }

    setSending(true);
    setResponse(null);
    setSendProgress(null); // Reset progress

    try {
      // Prepare recipients data
      const recipients = {
        phoneNumbers: allRecipients.filter(r => r.type === 'phone').map(r => r.value),
        contactIds: allRecipients.filter(r => r.type === 'contact').map(r => r.contact.id),
        groupIds: allRecipients.filter(r => r.type === 'group').map(r => r.value)
      };

      const formData = new FormData();
      formData.append('recipients', JSON.stringify(recipients));
      formData.append('message', trimmedMessage || '');
      formData.append('delay', messageDelay.toString()); // Add delay setting
      formData.append('accountId', selectedAccountId); // Add selected account ID
      
      // Log for debugging
      console.log('📤 Sending message with accountId:', selectedAccountId);
      
      if (selectedFile) {
        formData.append('file', selectedFile);
        formData.append('fileType', fileType);
      }

      const res = await api.post('/api/send-message', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 60000 // 60 seconds timeout for multiple messages
      });

      setResponse({ 
        type: 'success', 
        message: res.data.message || `Message sent to ${res.data.successCount || allRecipients.length} recipient(s)` 
      });
      setMessage(''); // Clear message after successful send
      handleRemoveFile(); // Clear file after successful send
      setSelectedRecipients([]); // Clear selected recipients
      setNumber('91'); // Reset phone number
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      } else {
      setResponse({ 
        type: 'error', 
        message: error.response?.data?.error || 'Failed to send message' 
      });
      }
    } finally {
      setSending(false);
    }
  };

  const handleScanQR = () => {
    // Open QR code page in new tab
    window.open('/qr', '_blank', 'width=800,height=900');
  };

  const getStatusColor = () => {
    switch (status.status) {
      case 'ready':
        return '#25D366'; // WhatsApp green
      case 'scan':
        return '#FFC107'; // Yellow
      case 'error':
      case 'auth_failure':
      case 'not_ready':
        return '#F44336'; // Red
      default:
        return '#2196F3'; // Blue
    }
  };

  return (
    <div className="App" style={{ display: 'flex', minHeight: '100vh' }}>
      <Sidebar onLogout={handleLogout} />
      <div className="container" style={{ 
        marginLeft: '260px', 
        width: 'calc(100% - 260px)',
        transition: 'all 0.3s ease',
        flex: 1,
        minHeight: '100vh'
      }}>
        <div className="header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <h1>📱 WhatsApp Message Sender</h1>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              {user && (
                <span style={{ color: '#666', fontSize: '0.9rem' }}>
                  👤 {user.user_id}
                </span>
              )}
            </div>
          </div>
          <div className="status-indicator" style={{ backgroundColor: getStatusColor() }}>
            <span className="status-dot"></span>
            <span className="status-text">{status.message}</span>
          </div>
        </div>

        {status.status !== 'ready' && (
          <div className="scan-button-container">
            <button onClick={handleScanQR} className="scan-qr-button">
              📷 Scan QR Code
            </button>
            <p className="scan-instruction">
              Click the button above to open QR code scanner in a new tab
            </p>
          </div>
        )}

        {status.status === 'ready' && (
          <div className="message-form-container">
            {/* Group Creation Modal */}
            {showGroupModal && (
              <div className="modal-overlay" onClick={() => setShowGroupModal(false)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3>Create New Group</h3>
                    <button 
                      className="close-modal-btn"
                      onClick={() => setShowGroupModal(false)}
                    >
                      ✕
                    </button>
                  </div>
                  <GroupManager 
                    contacts={contacts}
                    onGroupCreated={() => {
                      handleGroupCreated();
                      setShowGroupModal(false);
                    }}
                    compact={true}
                  />
                </div>
              </div>
            )}

            <form onSubmit={handleSendMessage} className="message-form">
              <div className="form-group">
                <div className="phone-label-wrapper">
                <label htmlFor="number">Phone Number</label>
                  <div className="phone-action-buttons">
                    <button
                      type="button"
                      className="phone-dropdown-btn-top"
                      onClick={() => setShowPhoneDropdown(!showPhoneDropdown)}
                      title="Select from groups or contacts"
                    >
                      +
                    </button>
                    <button
                      type="button"
                      className="phone-settings-btn"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        console.log('Settings button clicked, current state:', showSettings);
                        setShowSettings(!showSettings);
                      }}
                      title="Message sending settings"
                    >
                      ⚙️
                    </button>
                  </div>
                </div>
                {showSettings && (
                  <>
                    <div 
                      className="settings-overlay" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSettings(false);
                      }}
                    ></div>
                    <div 
                      className="settings-dropdown" 
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <div className="settings-header">
                        <h4>Message Sending Settings</h4>
                        <button
                          type="button"
                          className="close-settings-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowSettings(false);
                          }}
                        >
                          ✕
                        </button>
                      </div>
                      <div className="settings-content">
                        <div className="settings-item">
                          <label htmlFor="whatsapp-account">
                            Select WhatsApp Account
                          </label>
                          <select
                            id="whatsapp-account"
                            value={selectedAccountId}
                            onChange={(e) => {
                              e.stopPropagation();
                              setSelectedAccountId(e.target.value);
                            }}
                            onFocus={(e) => {
                              e.stopPropagation();
                            }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            className="settings-select"
                          >
                            {accounts.length === 0 ? (
                              <option value="default">No accounts available</option>
                            ) : (
                              accounts
                                .filter(acc => acc.isReady)
                                .map((account) => (
                                  <option key={account.accountId} value={account.accountId}>
                                    {account.phoneNumber || account.accountId} {account.isActive ? '(Active)' : ''}
                                  </option>
                                ))
                            )}
                          </select>
                          <small>Choose which WhatsApp account to use for sending messages</small>
                        </div>
                        <div className="settings-item">
                          <label htmlFor="message-delay">
                            Delay Between Messages (seconds)
                          </label>
                          <input
                            type="number"
                            id="message-delay"
                            min="0"
                            max="60"
                            value={messageDelay}
                            onChange={(e) => {
                              e.stopPropagation();
                              const value = e.target.value;
                              // Allow empty string while typing
                              if (value === '') {
                                setMessageDelay('');
                                return;
                              }
                              // Parse and validate
                              const numValue = parseInt(value);
                              if (!isNaN(numValue) && numValue >= 0) {
                                setMessageDelay(Math.min(60, numValue));
                              }
                            }}
                            onBlur={(e) => {
                              e.stopPropagation();
                              const value = e.target.value;
                              // If empty or invalid, set to 0
                              if (value === '' || isNaN(parseInt(value)) || parseInt(value) < 0) {
                                setMessageDelay(0);
                              } else {
                                const numValue = parseInt(value);
                                setMessageDelay(Math.max(0, Math.min(60, numValue)));
                              }
                            }}
                            onFocus={(e) => {
                              e.stopPropagation();
                            }}
                            onMouseDown={(e) => {
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onKeyDown={(e) => {
                              // Prevent negative numbers
                              if (e.key === '-' || e.key === '+' || e.key === 'e' || e.key === 'E') {
                                e.preventDefault();
                              }
                            }}
                            className="settings-input"
                          />
                          <small>Time to wait between sending messages to different recipients (0-60 seconds)</small>
                        </div>
                      </div>
                      <div className="settings-footer">
                        <button
                          type="button"
                          className="settings-cancel-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowSettings(false);
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="settings-submit-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Validate and ensure value is between 0-60
                            const finalValue = Math.max(0, Math.min(60, parseInt(messageDelay) || 0));
                            setMessageDelay(finalValue);
                            setShowSettings(false);
                          }}
                        >
                          Save Settings
                        </button>
                      </div>
                    </div>
                  </>
                )}
                <div className="phone-input-wrapper">
                <input
                  type="text"
                  id="number"
                  value={number}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNumber(value);
                    }}
                    onFocus={(e) => {
                      setShowPhoneDropdown(false);
                      // If input is empty, set to 91
                      if (!e.target.value || e.target.value.trim() === '') {
                        setNumber('91');
                      }
                    }}
                    onBlur={(e) => {
                      // Ensure 91 is at the start if user cleared the field
                      const value = e.target.value.trim();
                      if (!value || value === '') {
                        setNumber('91');
                      } else if (!value.startsWith('91')) {
                        // If user typed something without 91, add it
                        const cleaned = value.replace(/\D/g, '');
                        if (cleaned && !cleaned.startsWith('91')) {
                          setNumber('91' + cleaned);
                        }
                      }
                    }}
                    placeholder="Enter phone number or select from contacts/groups"
                  />
                  <div style={{ display: 'flex', gap: '5px', marginTop: '5px' }}>
                    <button
                      type="button"
                      onClick={handlePhoneNumberAdd}
                      className="add-phone-btn"
                      style={{ 
                        padding: '6px 12px', 
                        background: '#25D366', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      ➕ Add Number
                    </button>
                  </div>
                  {selectedRecipients.length > 0 && (
                    <div className="selected-recipients" style={{ marginTop: '10px' }}>
                      <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '5px' }}>
                        Selected Recipients ({selectedRecipients.length}):
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {selectedRecipients.map((recipient, index) => (
                          <div
                            key={index}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '6px 12px',
                              background: '#e8f5e9',
                              borderRadius: '20px',
                              fontSize: '0.85rem'
                            }}
                          >
                            <span>
                              {recipient.type === 'group' && '📁'}
                              {recipient.type === 'contact' && '👤'}
                              {recipient.type === 'phone' && '📱'}
                            </span>
                            <span>{recipient.label}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveRecipient(index)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '1rem',
                                color: '#d32f2f',
                                padding: '0',
                                width: '20px',
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {showPhoneDropdown && (
                    <div className="phone-dropdown-menu">
                      {/* Add New Group */}
                      <div
                        className="dropdown-item add-group-item"
                        onClick={() => {
                          setShowGroupModal(true);
                          setShowPhoneDropdown(false);
                        }}
                      >
                        <span className="dropdown-icon">📁</span>
                        <span>Add New Group</span>
                      </div>

                      {/* Existing Groups */}
                      {groups.length > 0 && (
                        <>
                          <div className="dropdown-divider"></div>
                          <div className="dropdown-section-title">Groups</div>
                          {groups.map(group => (
                            <div
                              key={group.id}
                              className="dropdown-item"
                              onClick={() => handleGroupSelect(group)}
                            >
                              <span className="dropdown-icon">📁</span>
                              <span>{group.name}</span>
                              <span className="dropdown-count">({group.contact_count || 0})</span>
                            </div>
                          ))}
                        </>
                      )}

                      {/* Saved Contacts */}
                      {contacts.length > 0 && (
                        <>
                          <div className="dropdown-divider"></div>
                          <div className="dropdown-section-title">Saved Contacts</div>
                          {contacts.map(contact => (
                            <div
                              key={contact.id}
                              className="dropdown-item"
                              onClick={() => handleContactSelect(contact)}
                            >
                              <span className="dropdown-icon">👤</span>
                              <span>{contact.name}</span>
                              <span className="dropdown-phone">{contact.phone_number}</span>
                            </div>
                          ))}
                        </>
                      )}

                      {groups.length === 0 && contacts.length === 0 && (
                        <div className="dropdown-empty">
                          <p>No groups or contacts yet</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <small>Format: Country code + Number (e.g., 919876543210 for India)</small>
              </div>

              <div className="form-group">
                <div className="templates-wrapper">
                  <div className="templates-label-wrapper">
                <label htmlFor="message">Message</label>
                    <div className="templates-buttons">
                      {templates.length > 0 && (
                        <button
                          type="button"
                          className="templates-dropdown-btn"
                          onClick={() => setShowTemplatesDropdown(!showTemplatesDropdown)}
                          title="Select template"
                        >
                          📋 Templates
                        </button>
                      )}
                      <button
                        type="button"
                        className="add-template-btn"
                        onClick={() => setShowTemplateModal(true)}
                        title="Add new template"
                      >
                        ➕ Add Template
                      </button>
                    </div>
                  </div>
                  {showTemplatesDropdown && templates.length > 0 && (
                    <div className="templates-dropdown-menu">
                      {templates.map(template => (
                        <div
                          key={template.id}
                          className="template-dropdown-item"
                          onClick={() => handleTemplateSelect(template)}
                        >
                          <span className="template-icon">
                            {template.file_type === 'image' && '🖼️'}
                            {template.file_type === 'audio' && '🎵'}
                            {template.file_type === 'video' && '🎬'}
                            {template.file_type === 'document' && '📄'}
                            {!template.file_type && '📝'}
                          </span>
                          <div className="template-info">
                            <span className="template-name">{template.name}</span>
                            {template.message && (
                              <span className="template-preview">
                                {template.message.substring(0, 50)}
                                {template.message.length > 50 ? '...' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message here... (Optional if sending media)"
                  rows="5"
                />
              </div>

              {/* Template Creation Modal */}
              {showTemplateModal && (
                <div className="template-modal-overlay" onClick={() => setShowTemplateModal(false)}>
                  <div className="template-modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="template-modal-header">
                      <div className="template-modal-title-section">
                        <div className="template-modal-icon">📝</div>
                        <div>
                          <h3>Create New Template</h3>
                          <p>Save reusable messages and files for quick access</p>
                        </div>
                      </div>
                      <button 
                        className="template-close-btn"
                        onClick={() => {
                          setShowTemplateModal(false);
                          setTemplateName('');
                          setTemplateMessage('');
                          handleRemoveTemplateFile();
                        }}
                        title="Close"
                      >
                        ✕
                      </button>
                    </div>
                    
                    <form onSubmit={handleCreateTemplate} className="template-form" noValidate>
                      <div className="template-form-group">
                        <label htmlFor="template-name">
                          <span className="label-icon">🏷️</span>
                          Template Name <span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          id="template-name"
                          value={templateName}
                          onChange={(e) => setTemplateName(e.target.value)}
                          placeholder="e.g., Welcome Message, Product Info, etc."
                  required
                          className="template-input"
                        />
                      </div>

                      <div className="template-form-group">
                        <label htmlFor="template-message">
                          <span className="label-icon">💬</span>
                          Template Message
                        </label>
                        <textarea
                          id="template-message"
                          value={templateMessage}
                          onChange={(e) => setTemplateMessage(e.target.value)}
                          placeholder="Enter your template message here... (Optional if file is attached)"
                          rows="5"
                          className="template-textarea"
                        />
                        <small className="template-hint">Leave empty if you only want to save a file</small>
                      </div>

                      <div className="template-form-group">
                        <label>
                          <span className="label-icon">📎</span>
                          Attach File (Optional)
                        </label>
                        <div className="template-file-upload-area">
                          <input
                            type="file"
                            id="template-file-input"
                            onChange={handleTemplateFileChange}
                            accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx"
                            style={{ display: 'none' }}
                          />
                          {!templateFile ? (
                            <label htmlFor="template-file-input" className="template-file-dropzone">
                              <div className="dropzone-icon">📤</div>
                              <div className="dropzone-text">
                                <strong>Click to upload</strong> or drag and drop
                              </div>
                              <div className="dropzone-hint">
                                Images, Audio, Video, Documents (PDF, DOC, etc.)
                              </div>
                            </label>
                          ) : (
                            <div className="template-file-preview-card">
                              <div className="template-file-preview-header">
                                <div className="template-file-preview-info">
                                  <span className="template-file-preview-icon">
                                    {templateFileType === 'image' && '🖼️'}
                                    {templateFileType === 'audio' && '🎵'}
                                    {templateFileType === 'video' && '🎬'}
                                    {templateFileType === 'document' && '📄'}
                                  </span>
                                  <div className="template-file-preview-details">
                                    <div className="template-file-preview-name">{templateFile.name}</div>
                                    <div className="template-file-preview-size">
                                      {(templateFile.size / 1024 / 1024).toFixed(2)} MB
                                    </div>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={handleRemoveTemplateFile}
                                  className="template-file-remove-btn"
                                  title="Remove file"
                                >
                                  ✕
                                </button>
                              </div>
                              {templateFilePreview && templateFileType === 'image' && (
                                <div className="template-image-preview">
                                  <img src={templateFilePreview} alt="Preview" />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <small className="template-hint">
                          <span className="hint-icon">ℹ️</span>
                          Either message or file (or both) must be provided
                        </small>
                      </div>

                      <div className="template-modal-footer">
                        <button
                          type="button"
                          className="template-cancel-btn"
                          onClick={() => {
                            setShowTemplateModal(false);
                            setTemplateName('');
                            setTemplateMessage('');
                            handleRemoveTemplateFile();
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="template-submit-btn"
                          disabled={isCreatingTemplate}
                          onClick={handleCreateTemplate}
                        >
                          {isCreatingTemplate ? (
                            <>
                              <span className="spinner-small"></span>
                              Creating...
                            </>
                          ) : (
                            <>
                              <span>✓</span>
                              Create Template
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="file-input">Attach File (Optional)</label>
                <div className="file-input-wrapper">
                  <input
                    type="file"
                    id="file-input"
                    onChange={handleFileChange}
                    accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx"
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="file-input" className="file-input-label">
                    📎 Choose File
                  </label>
                  {selectedFile && (
                    <div className="file-preview">
                      <div className="file-info">
                        <span className="file-icon">
                          {fileType === 'image' && '🖼️'}
                          {fileType === 'audio' && '🎵'}
                          {fileType === 'video' && '🎬'}
                          {fileType === 'document' && '📄'}
                        </span>
                        <span className="file-name">{selectedFile.name}</span>
                        <span className="file-size">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </span>
                        <button
                          type="button"
                          onClick={handleRemoveFile}
                          className="remove-file-btn"
                        >
                          ✕
                        </button>
                      </div>
                      {filePreview && fileType === 'image' && (
                        <div className="image-preview">
                          <img src={filePreview} alt="Preview" />
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <small>Supported: Images, Audio, Video, Documents (PDF, DOC, TXT, etc.)</small>
              </div>

              <button 
                type="submit" 
                className="send-button"
                disabled={sending || (!message && !selectedFile)}
              >
                {sending ? 'Sending...' : '📤 Send Message'}
              </button>
            </form>

            {/* Progress Bar */}
            {sendProgress && sendProgress.total > 1 && (
              <div className="message-progress-container">
                <div className="progress-header">
                  <span className="progress-title">
                    {sendProgress.status === 'waiting' && '⏳ Waiting...'}
                    {sendProgress.status === 'sending' && '📤 Sending...'}
                    {sendProgress.status === 'sent' && '✓ Sent'}
                    {sendProgress.status === 'error' && '✗ Error'}
                    {sendProgress.status === 'completed' && '✅ Completed'}
                    {sendProgress.status === 'starting' && '🚀 Starting...'}
                  </span>
                  <span className="progress-count">
                    {sendProgress.current} / {sendProgress.total}
                  </span>
                </div>
                <div className="progress-bar-wrapper">
                  <div 
                    className="progress-bar-fill"
                    style={{ width: `${sendProgress.percentage}%` }}
                  ></div>
                </div>
                {sendProgress.status === 'waiting' && sendProgress.waitProgress !== undefined && (
                  <div className="wait-progress">
                    <div className="wait-progress-bar-wrapper">
                      <div 
                        className="wait-progress-bar-fill"
                        style={{ width: `${sendProgress.waitProgress}%` }}
                      ></div>
                    </div>
                    <small>Waiting {sendProgress.waitTime}s before sending to {sendProgress.currentRecipient}...</small>
                  </div>
                )}
                {sendProgress.currentRecipient && sendProgress.status !== 'waiting' && (
                  <div className="current-recipient">
                    <small>Current: {sendProgress.currentRecipient}</small>
                  </div>
                )}
              </div>
            )}

            {response && (
              <div className={`response-message ${response.type}`}>
                {response.type === 'success' ? '✓' : '✗'} {response.message}
              </div>
            )}

            <div className="scan-button-container" style={{ marginTop: '20px' }}>
              <button onClick={handleScanQR} className="scan-qr-button-secondary">
                🔄 Scan New QR Code
              </button>
              <button 
                onClick={() => setShowApiInfo(!showApiInfo)} 
                className="api-info-button"
                style={{ marginTop: '10px' }}
              >
                {showApiInfo ? '🙈 Hide' : '🔗 Show'} API Endpoint
              </button>
            </div>

            {showApiInfo && apiInfo && (
              <div className="api-info-box">
                <h3>📡 API Endpoint Ready!</h3>
                <p>Use this endpoint in your external applications:</p>
                
                <div className="api-endpoint-item">
                  <label>GET Request:</label>
                  <code className="api-url">
                    {apiInfo.exampleGetUrl}
                  </code>
                  <button 
                    onClick={() => navigator.clipboard.writeText(apiInfo.exampleGetUrl)}
                    className="copy-button"
                  >
                    📋 Copy
                  </button>
                </div>

                <div className="api-endpoint-item">
                  <label>POST Request (JSON):</label>
                  <code className="api-url">
                    {apiInfo.apiEndpoint}
                  </code>
                  <pre className="api-example">
{`POST ${apiInfo.apiEndpoint}
Content-Type: application/json

{
  "mobile": "919876543210",
  "msg": "Your message here"
}`}
                  </pre>
                  <button 
                    onClick={() => navigator.clipboard.writeText(apiInfo.apiEndpoint)}
                    className="copy-button"
                  >
                    📋 Copy URL
                  </button>
                </div>

                {apiInfo.activePhoneNumber && (
                  <div className="api-endpoint-item">
                    <label>📱 Active Phone Number:</label>
                    <code className="api-url">{apiInfo.activePhoneNumber}</code>
                  </div>
                )}

                {apiInfo.scannedPhoneNumber && !apiInfo.activePhoneNumber && (
                  <div className="api-endpoint-item">
                    <label>📱 Scanned Phone Number:</label>
                    <code className="api-url">{apiInfo.scannedPhoneNumber}</code>
                  </div>
                )}

                <div className="api-response-example">
                  <strong>Response Format:</strong>
                  <pre>
{`{
  "status": "success",
  "errormsg": "1 saved sucessfully",
  "statuscode": 200,
  "requestid": "message_id",
  "msgcount": 1,
  "msgcost": 1,
  "activeAccountId": "${apiInfo.activeAccountId || 'default'}",
  "activePhoneNumber": "${apiInfo.activePhoneNumber || 'N/A'}"
}`}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default HomePage;

